import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import { clientsAPI, serviceConfigsAPI, getAuthToken } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, Building, X, Edit, Eye, Upload, Download, FileText, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import DocumentsPage from './DocumentsPage';
import { ClientFormDialog } from './components/ClientFormDialog';
import { ClientViewDialog } from './components/ClientViewDialog';
import { BulkUploadDialog } from './components/BulkUploadDialog';
import logger from '@/lib/logger';
import SortableHeader, { sortBy } from '@/components/SortableHeader';

const SERVICE_COLORS = {
  'GST Returns': 'bg-emerald-100 text-emerald-700',
  'Income Tax': 'bg-blue-100 text-blue-700',
  'TDS Returns': 'bg-violet-100 text-violet-700',
  'Audit': 'bg-amber-100 text-amber-700',
  'Tax Audit': 'bg-amber-100 text-amber-700',
  'Statutory Audit': 'bg-orange-100 text-orange-700',
  'Trust Audit': 'bg-yellow-100 text-yellow-700',
  'ROC Compliance': 'bg-rose-100 text-rose-700',
  'Advisory': 'bg-cyan-100 text-cyan-700',
};

const emptyForm = {
  serial_no: '', name: '', email: '', phone: '', pan: '', tan: '',
  gstins: [{ number: '', category: 'Regular', registration_date: '', cancellation_date: '' }], address: '', company_name: '', legal_status: '', dob_doi: '',
  services: [], notes: '', tags: []
};

const ClientsPage = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterSerialNo, setFilterSerialNo] = useState('');
  const [filterContact, setFilterContact] = useState('');
  const [filterPanTan, setFilterPanTan] = useState('');
  const [filterGstin, setFilterGstin] = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDob, setFilterDob] = useState('');
  const [sort, setSort] = useState({ field: 'name', dir: 'asc' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewClient, setViewClient] = useState(null);
  const [editMode, setEditMode] = useState(false); // false = create, true = edit
  const [editingClientId, setEditingClientId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [documentsClient, setDocumentsClient] = useState(null);
  const [serviceConfigs, setServiceConfigs] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // null = delete all, {id, name} = single
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';
  const canManage = ['admin', 'staff'].includes(user?.role);

  const loadClients = useCallback(async () => {
    try {
      const res = await clientsAPI.list();
      setClients(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, []);

  const loadServiceConfigs = useCallback(async () => {
    try {
      const res = await serviceConfigsAPI.list();
      setServiceConfigs(res.data);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { loadClients(); loadServiceConfigs(); }, [loadClients, loadServiceConfigs]);

  const validateForm = () => {
    if (!form.name.trim()) return 'Client name is required';
    if (!form.pan?.trim() && !form.tan?.trim()) return 'Either PAN or TAN number is required';
    return '';
  };

  const openCreate = () => {
    setForm({ ...emptyForm, gstins: [{ number: '', category: 'Regular', registration_date: '', cancellation_date: '' }] });
    setEditMode(false);
    setEditingClientId(null);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setForm({
      serial_no: client.serial_no || '',
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      pan: client.pan || '',
      tan: client.tan || '',
      gstins: client.gstins?.length > 0
        ? client.gstins.map(g => typeof g === 'string'
            ? { number: g, category: 'Regular', registration_date: '', cancellation_date: '' }
            : {
                number: g.number || '',
                category: g.category || 'Regular',
                registration_date: g.registration_date || '',
                cancellation_date: g.cancellation_date || '',
              })
        : [{ number: '', category: 'Regular', registration_date: '', cancellation_date: '' }],
      address: client.address || '',
      company_name: client.company_name || '',
      legal_status: client.legal_status || '',
      dob_doi: client.dob_doi || '',
      services: client.services || [],
      notes: client.notes || '',
      tags: client.tags || [],
    });
    setEditMode(true);
    setEditingClientId(client.id);
    setFormError('');
    setDialogOpen(true);
  };

  const openView = (client) => {
    setViewClient(client);
    setViewDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSubmitting(true);

    // Clean GSTINs: remove empty entries; pass through dates only when set
    const cleanGstins = form.gstins
      .filter(g => (g.number || '').trim())
      .map(g => {
        const entry = { number: g.number.trim().toUpperCase(), category: g.category || 'Regular' };
        if (g.registration_date) entry.registration_date = g.registration_date;
        if (g.cancellation_date) entry.cancellation_date = g.cancellation_date;
        return entry;
      });
    const payload = { ...form, gstins: cleanGstins };

    try {
      if (editMode) {
        await clientsAPI.update(editingClientId, payload);
        toast.success('Client updated successfully');
      } else {
        await clientsAPI.create(payload);
        toast.success('Client created successfully');
      }
      setDialogOpen(false);
      setForm({ ...emptyForm });
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save client');
    }
    setSubmitting(false);
  };

  // GSTIN management
  const addGstin = () => setForm({ ...form, gstins: [...form.gstins, { number: '', category: 'Regular', registration_date: '', cancellation_date: '' }] });
  const removeGstin = (idx) => {
    const updated = form.gstins.filter((_, i) => i !== idx);
    setForm({ ...form, gstins: updated.length > 0 ? updated : [{ number: '', category: 'Regular', registration_date: '', cancellation_date: '' }] });
  };
  const updateGstin = (idx, field, value) => {
    const updated = [...form.gstins];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, gstins: updated });
  };

  // Services toggle
  const toggleService = (service) => {
    const current = form.services || [];
    if (current.includes(service)) {
      setForm({ ...form, services: current.filter(s => s !== service) });
    } else {
      setForm({ ...form, services: [...current, service] });
    }
  };

  // Tags toggle
  const toggleTag = (tag) => {
    const current = form.tags || [];
    if (current.includes(tag)) {
      setForm({ ...form, tags: current.filter(t => t !== tag) });
    } else {
      setForm({ ...form, tags: [...current, tag] });
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await clientsAPI.delete(deleteTarget.id);
      toast.success(`Client "${deleteTarget.name}" deleted`);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete client');
    }
    setDeleting(false);
  };

  const handleDeleteAllClients = async () => {
    setDeleting(true);
    try {
      const res = await clientsAPI.deleteAll();
      toast.success(res.data.message || 'All clients deleted');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete clients');
    }
    setDeleting(false);
  };


  // Bulk upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/bulk-upload/template`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'client_bulk_upload_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded successfully');
    } catch (error) {
      logger.error(error);
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error('Please select a file');
      return;
    }

    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      setBulkResult(result);
      
      if (result.successful > 0) {
        toast.success(`Successfully imported ${result.successful} clients`);
        loadClients();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} rows failed to import`);
      }
    } catch (error) {
      logger.error(error);
      toast.error(error.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkFile(null);
    setBulkResult(null);
  };


  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const matchGlobal = !q || [c.name, c.company_name, c.email, c.pan, c.tan, ...(c.gstins || []).map(g => typeof g === 'string' ? g : g.number)].some(v => v?.toLowerCase().includes(q));
    const matchName = !filterName || c.name?.toLowerCase().includes(filterName.toLowerCase()) || c.company_name?.toLowerCase().includes(filterName.toLowerCase());
    const matchSerialNo = !filterSerialNo || (c.serial_no || '').toLowerCase().includes(filterSerialNo.toLowerCase());
    const matchContact = !filterContact || c.email?.toLowerCase().includes(filterContact.toLowerCase()) || c.phone?.includes(filterContact);
    const matchPanTan = !filterPanTan || c.pan?.toLowerCase().includes(filterPanTan.toLowerCase()) || c.tan?.toLowerCase().includes(filterPanTan.toLowerCase());
    const matchGstin = !filterGstin || c.gstins?.some(g => (typeof g === 'string' ? g : g.number || '').toLowerCase().includes(filterGstin.toLowerCase()));
    const matchStatus = !filterStatus || (c.legal_status || '').toLowerCase().includes(filterStatus.toLowerCase());
    const matchDob = !filterDob || (c.dob_doi || '').includes(filterDob);
    const matchService = !filterService || c.services?.some(s => s.toLowerCase().includes(filterService.toLowerCase()));
    const matchTag = !filterTag || c.tags?.some(t => t.toLowerCase().includes(filterTag.toLowerCase()));
    return matchGlobal && matchSerialNo && matchName && matchContact && matchPanTan && matchGstin && matchStatus && matchDob && matchService && matchTag;
  });

  // Apply sorting
  const sortedFiltered = (() => {
    if (!sort.field || !sort.dir) return filtered;
    const getter = (c) => {
      switch (sort.field) {
        case 'serial_no': return c.serial_no;
        case 'name': return c.name || c.company_name;
        case 'contact': return c.email || c.phone;
        case 'pan': return c.pan || c.tan;
        case 'gstin': return c.gstins?.[0] ? (typeof c.gstins[0] === 'string' ? c.gstins[0] : c.gstins[0].number) : null;
        case 'status': return c.legal_status;
        case 'dob': return c.dob_doi;
        case 'services': return (c.services || []).join(', ');
        case 'tags': return (c.tags || []).join(', ');
        default: return null;
      }
    };
    return sortBy(filtered, getter, sort.dir);
  })();

  const hasColumnFilters = filterSerialNo || filterName || filterContact || filterPanTan || filterGstin || filterStatus || filterDob || filterService || filterTag;
  const clearColumnFilters = () => { setFilterSerialNo(''); setFilterName(''); setFilterContact(''); setFilterPanTan(''); setFilterGstin(''); setFilterStatus(''); setFilterDob(''); setFilterService(''); setFilterTag(''); };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Clients</h2>
          <p className="text-sm text-muted-foreground mt-1">{clients.length} total clients</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, PAN, TAN, GSTIN..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 w-72"
              data-testid="client-search"
            />
          </div>
          {canManage && (
            <div className="flex gap-2">
              {isAdmin && (
                <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-button">
                  <Download className="h-4 w-4 mr-2" /> Download Template
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="add-client-dropdown">
                    <Plus className="h-4 w-4 mr-2" /> Add Clients
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={openCreate} data-testid="add-single-client">
                    <Plus className="h-4 w-4 mr-2" /> Add Single Client
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBulkDialogOpen(true)} data-testid="bulk-upload-client">
                    <Upload className="h-4 w-4 mr-2" /> Bulk Upload
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}
        </div>
      ) : sortedFiltered.length === 0 ? (
        <Card className="border-dashed border-2 bg-gradient-to-b from-muted/20 to-transparent">
          <CardContent className="p-10 sm:p-14 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-muted/40 flex items-center justify-center mb-5">
              {(search || hasColumnFilters) ? <Search className="h-7 w-7 text-muted-foreground" /> : <Building className="h-7 w-7 text-muted-foreground" />}
            </div>
            <h3 className="text-lg font-semibold mb-1.5" style={{ fontFamily: 'Space Grotesk' }}>
              {(search || hasColumnFilters) ? 'No matching clients' : 'No clients yet'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {(search || hasColumnFilters)
                ? <>We searched all <span className="font-medium text-foreground">{clients.length}</span> clients but none match your current filters.</>
                : 'Add your first client to start tracking compliances and assignments.'}
            </p>

            {/* Active filter chips — one-click removal so the user can fix
                the no-result state without scrolling back to the filter row */}
            {(search || hasColumnFilters) && (
              <div className="flex flex-wrap gap-1.5 justify-center mt-5">
                {search && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    Search: "{search}"
                    <button type="button" onClick={() => setSearch('')} className="ml-1 hover:text-foreground" data-testid="empty-clear-search"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterSerialNo && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    S.No: {filterSerialNo}
                    <button type="button" onClick={() => setFilterSerialNo('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterName && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    Name: {filterName}
                    <button type="button" onClick={() => setFilterName('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterContact && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    Contact: {filterContact}
                    <button type="button" onClick={() => setFilterContact('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterPanTan && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    PAN/TAN: {filterPanTan}
                    <button type="button" onClick={() => setFilterPanTan('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterGstin && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    GSTIN: {filterGstin}
                    <button type="button" onClick={() => setFilterGstin('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterStatus && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    Status: {filterStatus}
                    <button type="button" onClick={() => setFilterStatus('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterDob && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    DOB: {filterDob}
                    <button type="button" onClick={() => setFilterDob('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterService && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    Service: {filterService}
                    <button type="button" onClick={() => setFilterService('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterTag && (
                  <Badge variant="secondary" className="gap-1 h-7 px-2.5 text-xs">
                    Tag: {filterTag}
                    <button type="button" onClick={() => setFilterTag('')} className="ml-1"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-wrap gap-2 justify-center mt-6">
              {(search || hasColumnFilters) ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSearch(''); clearColumnFilters(); }}
                    data-testid="empty-clear-all"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Clear all filters
                  </Button>
                  {canManage && (
                    <Button size="sm" onClick={openCreate} data-testid="empty-add-client">
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      {search ? `Add "${search}" as a new client` : 'Add a new client'}
                    </Button>
                  )}
                </>
              ) : (
                canManage && (
                  <Button onClick={openCreate} data-testid="empty-add-first-client">
                    <Plus className="h-4 w-4 mr-1" /> Add your first client
                  </Button>
                )
              )}
            </div>

            {/* Tiny help line */}
            {(search || hasColumnFilters) && (
              <p className="text-xs text-muted-foreground mt-4">
                Tip: search matches name, PAN/TAN, GSTIN, contact, and email — try a partial value
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24"><SortableHeader field="serial_no" sort={sort} onChange={setSort}>Genius S.No.</SortableHeader></TableHead>
                <TableHead><SortableHeader field="name" sort={sort} onChange={setSort}>Name</SortableHeader></TableHead>
                <TableHead className="hidden sm:table-cell"><SortableHeader field="contact" sort={sort} onChange={setSort}>Email / Phone</SortableHeader></TableHead>
                <TableHead className="hidden md:table-cell"><SortableHeader field="pan" sort={sort} onChange={setSort}>PAN / TAN</SortableHeader></TableHead>
                <TableHead className="hidden lg:table-cell"><SortableHeader field="gstin" sort={sort} onChange={setSort}>GSTINs</SortableHeader></TableHead>
                <TableHead className="hidden lg:table-cell"><SortableHeader field="status" sort={sort} onChange={setSort}>Status</SortableHeader></TableHead>
                <TableHead className="hidden xl:table-cell"><SortableHeader field="dob" sort={sort} onChange={setSort}>DOB / DOI</SortableHeader></TableHead>
                <TableHead className="hidden xl:table-cell"><SortableHeader field="services" sort={sort} onChange={setSort}>Services</SortableHeader></TableHead>
                <TableHead className="hidden 2xl:table-cell"><SortableHeader field="tags" sort={sort} onChange={setSort}>Tags</SortableHeader></TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="py-1.5">
                  <Input placeholder="Filter S.No..." value={filterSerialNo} onChange={e => setFilterSerialNo(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-serial-no" />
                </TableHead>
                <TableHead className="py-1.5">
                  <Input placeholder="Filter name..." value={filterName} onChange={e => setFilterName(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-name" />
                </TableHead>
                <TableHead className="hidden sm:table-cell py-1.5">
                  <Input placeholder="Filter email/phone..." value={filterContact} onChange={e => setFilterContact(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-contact" />
                </TableHead>
                <TableHead className="hidden md:table-cell py-1.5">
                  <Input placeholder="Filter PAN/TAN..." value={filterPanTan} onChange={e => setFilterPanTan(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-pantan" />
                </TableHead>
                <TableHead className="hidden lg:table-cell py-1.5">
                  <Input placeholder="Filter GSTIN..." value={filterGstin} onChange={e => setFilterGstin(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-gstin" />
                </TableHead>
                <TableHead className="hidden lg:table-cell py-1.5" >
                  <Input placeholder="Filter status..." value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-status" />
                </TableHead>
                <TableHead className="hidden xl:table-cell py-1.5">
                  <Input placeholder="Filter DOB/DOI..." value={filterDob} onChange={e => setFilterDob(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-dob" />
                </TableHead>
                <TableHead className="hidden xl:table-cell py-1.5">
                  <Input placeholder="Filter service..." value={filterService} onChange={e => setFilterService(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-service" />
                </TableHead>
                <TableHead className="hidden 2xl:table-cell py-1.5">
                  <Input placeholder="Filter tag..." value={filterTag} onChange={e => setFilterTag(e.target.value)} className="h-7 text-xs bg-background" data-testid="filter-tag" />
                </TableHead>
                <TableHead className="py-1.5">
                  {hasColumnFilters && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={clearColumnFilters} title="Clear filters" data-testid="clear-column-filters">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedFiltered.map(client => (
                <TableRow key={client.id} data-testid="client-row">
                  <TableCell className="text-sm text-muted-foreground font-mono">{client.serial_no || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                        {client.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{client.name}</p>
                        {client.company_name && <p className="text-xs text-muted-foreground">{client.company_name}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="space-y-0.5">
                      {client.email && <p className="text-xs text-muted-foreground">{client.email}</p>}
                      {client.phone && <p className="text-xs text-muted-foreground">{client.phone}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="space-y-0.5">
                      {client.pan && <p className="text-xs"><span className="text-muted-foreground">PAN:</span> {client.pan}</p>}
                      {client.tan && <p className="text-xs"><span className="text-muted-foreground">TAN:</span> {client.tan}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(client.gstins || []).map((g, i) => {
                        const num = typeof g === 'string' ? g : g.number;
                        const cat = typeof g === 'string' ? '' : g.category;
                        return (
                          <span key={num || `tbl-gstin-${i}`} className="inline-flex items-center gap-1">
                            <Badge variant="secondary" className="text-[10px] font-mono">{num}</Badge>
                            {cat && <span className="text-[9px] text-muted-foreground">({cat})</span>}
                          </span>
                        );
                      })}
                      {(!client.gstins || client.gstins.length === 0) && <span className="text-xs text-muted-foreground">-</span>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-xs">{client.legal_status || '-'}</span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <span className="text-xs">{client.dob_doi || '-'}</span>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(client.services || []).map(s => (
                        <Badge key={s} variant="secondary" className={`text-[10px] ${SERVICE_COLORS[s] || ''}`}>{s}</Badge>
                      ))}
                      {(!client.services || client.services.length === 0) && <span className="text-xs text-muted-foreground">-</span>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden 2xl:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(client.tags || []).map(t => (
                        <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                      ))}
                      {(!client.tags || client.tags.length === 0) && <span className="text-xs text-muted-foreground">-</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setDocumentsClient(client); setDocumentsDialogOpen(true); }} data-testid="documents-client-button">
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openView(client)} data-testid="view-client-button">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {canManage && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(client)} data-testid="edit-client-button">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => { setDeleteTarget({ id: client.id, name: client.name }); setDeleteConfirmOpen(true); }} data-testid={`delete-client-${client.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <ClientFormDialog
        open={dialogOpen} onOpenChange={setDialogOpen} editMode={editMode}
        form={form} setForm={setForm} onSubmit={handleSubmit}
        formError={formError} submitting={submitting} canManage={canManage}
        addGstin={addGstin} removeGstin={removeGstin} updateGstin={updateGstin}
        toggleService={toggleService} toggleTag={toggleTag}
        serviceConfigs={serviceConfigs}
        editingClientId={editingClientId}
      />

      {/* View Client Detail Dialog */}
      <ClientViewDialog
        open={viewDialogOpen} onOpenChange={setViewDialogOpen}
        client={viewClient} canManage={canManage} onEdit={openEdit}
      />

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        open={bulkDialogOpen} onClose={closeBulkDialog}
        file={bulkFile} setFile={setBulkFile}
        onUpload={handleBulkUpload} uploading={bulkUploading} result={bulkResult}
      />

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {documentsClient && (
            <DocumentsPage clientId={documentsClient.id} clientName={documentsClient.name} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={(open) => { if (!deleting) { setDeleteConfirmOpen(open); if (!open) setDeleteTarget(null); } }}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold">
                {deleteTarget ? 'Delete Client' : 'Delete All Clients'}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                {deleteTarget
                  ? <>Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will remove all their subtask assignments, invoices, documents, and related data.</>
                  : <>Are you sure you want to delete <strong>all {clients.length} clients</strong>? This will remove all subtask assignments, invoices, documents, and related data. This action cannot be undone.</>
                }
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <Button variant="outline" className="flex-1" onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }} disabled={deleting} data-testid="cancel-delete">
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={deleteTarget ? handleDeleteClient : handleDeleteAllClients}
                disabled={deleting}
                data-testid="confirm-delete"
              >
                {deleting ? 'Deleting...' : deleteTarget ? 'Delete Client' : 'Delete All Clients'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
